import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import axiosInstance from '../../api/axiosInstance';
import toast from 'react-hot-toast';
import { DocumentArrowDownIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';

interface ProductsImporterProps {
  onImportSuccess?: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface ImportResult {
  lote: number;
  created: number;
  skipped: number;
  failed: number;
  skippedProducts: any[];
  processed: any[];
}

const BATCH_SIZE = 15;

// Utilidad para convertir las claves de un objeto a minúsculas
const toLowerCaseKeys = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.map(toLowerCaseKeys);
  } else if (obj && typeof obj === 'object') {
    const newObj: any = {};
    Object.keys(obj).forEach(key => {
      const lowerKey = key.toLowerCase();
      newObj[lowerKey] = toLowerCaseKeys(obj[key]);
    });
    return newObj;
  }
  return obj;
};

const ProductsImporter: React.FC<ProductsImporterProps> = ({ onImportSuccess }) => {
  const [importMode, setImportMode] = useState<'createAndUpdate' | 'updateOnly'>('createAndUpdate');
  const [importFile, setImportFile] = useState<File | null>(null);
  const [skippedProducts, setSkippedProducts] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState('');
  const [processedProducts, setProcessedProducts] = useState<any[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [totalToProcess, setTotalToProcess] = useState(0); // Reservado para uso futuro
  const [isFinished, setIsFinished] = useState(false);
  const [showFields, setShowFields] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setImportFile(e.target.files?.[0] || null);
    setSkippedProducts([]);
    setProcessedProducts([]);
    setProgress(0);
    setProgressText('');
    setIsFinished(false);
  };

  const handleImport = async () => {
    if (!importFile) {
      toast.error('Selecciona un archivo Excel');
      return;
    }
    setIsLoading(true);
    setSkippedProducts([]);
    setProcessedProducts([]);
    setProgress(0);
    setProgressText('');
    setIsFinished(false);
    try {
      const data = await importFileToJson(importFile);
      setTotalToProcess(data.length);
      const batches = [];
      for (let i = 0; i < data.length; i += BATCH_SIZE) {
        batches.push(data.slice(i, i + BATCH_SIZE));
      }
      let allSkipped: any[] = [];
      let allProcessed: any[] = [];
      for (let i = 0; i < batches.length; i++) {
        setProgressText(`Procesando lote ${i + 1} de ${batches.length}`);
        // Convertir claves a minúsculas antes de enviar
        const batch = toLowerCaseKeys(batches[i]);
        try {
          let response;
          if (importMode === 'updateOnly') {
            response = await axiosInstance.put('/products', batch);
          } else {
            response = await axiosInstance.post('/products', batch);
          }
          const res = response.data;
          // Acumular productos omitidos
          if (res.skippedProducts && res.skippedProducts.length > 0) {
            allSkipped = allSkipped.concat(res.skippedProducts);
          }
          // Acumular productos procesados (éxito y omitidos)
          allProcessed = allProcessed.concat(batch.map((p: any) => ({
            sku: p.sku,
            name: p.name,
            status: res.skippedProducts?.find((s: any) => s.sku === p.sku) ? 'Omitido' : 'Procesado'
          })));
        } catch (error) {
          // Si falla todo el lote, marcar todos como fallidos
          allSkipped = allSkipped.concat(batch.map((p: any) => ({ sku: p.sku, reason: 'Error en el lote' })));
          allProcessed = allProcessed.concat(batch.map((p: any) => ({ sku: p.sku, name: p.name, status: 'Error' })));
        }
        setSkippedProducts([...allSkipped]);
        setProcessedProducts([...allProcessed]);
        setProgress(Math.round(((i + 1) / batches.length) * 100));
        await new Promise(res => setTimeout(res, 200)); // Pequeño delay para UX
      }
      setIsFinished(true);
      toast.success('Importación finalizada');
      if (onImportSuccess) onImportSuccess();
    } catch (error) {
      toast.error('Error al importar productos');
    } finally {
      setIsLoading(false);
      setProgressText('');
    }
  };

  const importFileToJson = async (file: File) => {
    return new Promise<any[]>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (evt) => {
        const bstr = evt.target?.result as string;
        const wb = XLSX.read(bstr, { type: 'binary' });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws);
        resolve(data);
      };
      reader.readAsBinaryString(file);
    });
  };

  const handleDownloadExcel = () => {
    if (skippedProducts.length === 0) return;
    const ws = XLSX.utils.json_to_sheet(skippedProducts);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'No Importados');
    XLSX.writeFile(wb, 'productos_no_importados.xlsx');
  };

  const handleDownloadTemplate = () => {
    const templateData = [
      {
        product_id: '',
        sku: 'SKU-001',
        name: 'Nombre del producto',
        description: 'Descripción opcional',
        category_id: '',
        price: 0,
        tax_rate: 0,
        ean12: '',
        peso: 0,
        longitud: 0,
        ancho: 0,
        alto: 0,
        instrucciones_empaque: '',
        imagen: '',
        is_combo: false,
        components: '[{"sku": "SKU-COMP-1", "quantity": 1}]'
      }
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Formato');
    XLSX.writeFile(wb, 'formato_importacion_productos.xlsx');
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] w-full">
      <div className="bg-white shadow-lg rounded-xl p-8 max-w-lg w-full border border-gray-100">
        <div className="flex flex-col items-center mb-6">
          <div className="bg-blue-100 rounded-full p-3 mb-2">
            <DocumentArrowDownIcon className="h-8 w-8 text-blue-600" />
          </div>
          <h2 className="text-2xl font-bold text-custom-blue mb-1">Importar productos</h2>
          <p className="text-gray-500 text-center text-sm max-w-md mb-2">
            Sube un archivo (.xlsx) con los productos que deseas crear o actualizar.
          </p>
          {/* Acordeón de campos válidos */}
          <div className="mb-4 w-full">
            <button
              type="button"
              className="flex items-center justify-between w-full px-3 py-2 bg-blue-50 border border-blue-100 rounded cursor-pointer hover:bg-blue-100 transition-colors"
              onClick={() => setShowFields((prev) => !prev)}
              aria-expanded={showFields}
            >
              <span className="font-semibold text-blue-700">Campos válidos para la importación</span>
              <svg
                className={`h-5 w-5 text-blue-700 transform transition-transform ${showFields ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {showFields && (
              <div className="p-3 text-xs text-left bg-blue-50 border-t border-blue-100 rounded-b">
                <ul className="list-disc pl-5 mt-1 text-blue-900">
                  <li><b>product_id</b> (opcional, para actualizar)</li>
                  <li><b>sku</b> (requerido)</li>
                  <li><b>name</b> (requerido)</li>
                  <li><b>description</b></li>
                  <li><b>category_id</b></li>
                  <li><b>price</b></li>
                  <li><b>tax_rate</b></li>
                  <li><b>ean12</b></li>
                  <li><b>peso</b></li>
                  <li><b>longitud</b></li>
                  <li><b>ancho</b></li>
                  <li><b>alto</b></li>
                  <li><b>instrucciones_empaque</b></li>
                  <li><b>imagen</b></li>
                  <li><b>is_combo</b> (true/false)</li>
                  <li><b>components</b> (array de objetos con <b>sku</b> y <b>quantity</b>)</li>
                </ul>
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-4 items-center mb-4">
          <button
            type="button"
            onClick={handleDownloadTemplate}
            className="w-full flex items-center justify-center gap-2 py-2 px-4 border border-blue-200 text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-all text-sm font-semibold"
          >
            <ArrowDownTrayIcon className="h-5 w-5" />
            Descargar formato de importación
          </button>
          <input
            type="file"
            accept=".xlsx"
            onChange={handleFileChange}
            className="block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={importMode === 'updateOnly'}
              onChange={() => setImportMode(importMode === 'updateOnly' ? 'createAndUpdate' : 'updateOnly')}
              className="accent-blue-600"
            />
            <span className="text-gray-700">Solo actualizar existentes</span>
          </label>
          <button
            onClick={handleImport}
            className="w-full py-2 px-4 bg-custom-blue text-white rounded-lg shadow hover:bg-blue-700 transition-all font-semibold disabled:opacity-60"
            disabled={isLoading}
          >
            {isLoading ? 'Importando...' : 'Importar productos'}
          </button>
        </div>
        {/* Barra de progreso visual */}
        {isLoading && (
          <div className="w-full my-4">
            <div className="w-full bg-gray-200 rounded-full h-4">
              <div
                className="bg-blue-500 h-4 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <div className="text-center text-xs text-gray-600 mt-1">{progress}%</div>
            <div className="text-center text-xs text-gray-500 mt-1">{progressText}</div>
          </div>
        )}
        {/* Productos procesados en tiempo real */}
        {processedProducts.length > 0 && (
          <div className="mt-4 max-h-40 overflow-y-auto border border-gray-100 rounded bg-gray-50 p-2 text-xs">
            <div className="font-semibold text-gray-700 mb-1">Productos procesados:</div>
            <ul className="grid grid-cols-1 gap-1">
              {processedProducts.map((prod, idx) => (
                <li key={idx} className={
                  prod.status === 'Procesado' ? 'text-green-700' : prod.status === 'Omitido' ? 'text-yellow-700' : 'text-red-700'
                }>
                  {prod.sku} - {prod.name} <span className="font-semibold">[{prod.status}]</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {/* Reporte final y descarga de Excel */}
        {isFinished && skippedProducts.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mt-4">
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold text-yellow-700">Productos no importados:</div>
              <button
                onClick={handleDownloadExcel}
                className="flex items-center gap-1 px-2 py-1 bg-yellow-200 text-yellow-900 rounded hover:bg-yellow-300 text-xs font-semibold"
              >
                <ArrowDownTrayIcon className="h-4 w-4" /> Descargar Excel
              </button>
            </div>
            <ul className="list-disc pl-5 max-h-32 overflow-y-auto text-yellow-800 text-xs">
              {skippedProducts.map((prod, idx) => (
                <li key={idx}>
                  SKU: <span className="font-mono">{prod.sku}</span> - {prod.reason}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductsImporter; 